/**
 * Reducer + initial-state builder for the AIChatConversation slice.
 *
 * `mode` is URL-derived (detectConversationMode) — the composition root computes it once and passes it
 * as an override; this builder falls back to detecting it again for direct callers/tests.
 *
 * SET_CONVERSATION_MODE has reset-on-mode-change semantics: only when mode actually changes does it
 * clear currentConversationId + messageQueue and reset streamStatus/isLoadingConversation; an
 * unchanged mode returns state untouched.
 */
import { CONVERSATION_MODES, type ConversationMode } from '../../constants/modes';
import { STREAM_STATUS } from '../../constants/status';
import type { ConversationAction, ConversationState } from './aiChatConversationTypes';

export const CONVERSATION_ACTIONS = {
  SET_CHAT_CONTEXT: 'SET_CHAT_CONTEXT',
  SET_CURRENT_CONVERSATION_ID: 'SET_CURRENT_CONVERSATION_ID',
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  SET_STREAM_STATUS: 'SET_STREAM_STATUS',
  SET_CONVERSATION_MODE: 'SET_CONVERSATION_MODE',
  QUEUE_MESSAGE: 'QUEUE_MESSAGE',
  DEQUEUE_MESSAGE: 'DEQUEUE_MESSAGE',
  CLEAR_MESSAGE_QUEUE: 'CLEAR_MESSAGE_QUEUE',
  SET_CANVAS_CONTENT: 'SET_CANVAS_CONTENT',
  SET_CONVERSATION_DATA: 'SET_CONVERSATION_DATA',
  CLEAR_CONVERSATION_DATA: 'CLEAR_CONVERSATION_DATA',
  SET_CANVAS_TOOL_EXECUTING: 'SET_CANVAS_TOOL_EXECUTING',
  SET_IS_LOADING_CONVERSATION: 'SET_IS_LOADING_CONVERSATION',
  SET_LAST_FAILED_MESSAGE: 'SET_LAST_FAILED_MESSAGE',
  CLEAR_LAST_FAILED_MESSAGE: 'CLEAR_LAST_FAILED_MESSAGE',
  ADD_PILL: 'ADD_PILL',
  REMOVE_PILL: 'REMOVE_PILL',
  REMOVE_PILLS_BY_TYPE: 'REMOVE_PILLS_BY_TYPE',
  CLEAR_PILLS: 'CLEAR_PILLS',
} as const;

/**
 * Detect chatbot mode based on URL pattern — identical to the check that lived in
 * getInitialChatbotState. Kept here so the builder can supply the URL-derived default for direct
 * callers/tests; the composition root still computes it once and passes it as an override.
 */
export function detectConversationMode(pathname: string): ConversationMode {
  if (pathname.startsWith('/activities/sessions/')) return CONVERSATION_MODES.PRACTICE;
  if (pathname.startsWith('/teacher-tools/')) return CONVERSATION_MODES.TEACHER_TOOLS;
  if (pathname.startsWith('/results-assesment-individual/') || pathname.includes('/question/')) return CONVERSATION_MODES.REVISION;
  return CONVERSATION_MODES.STUDY;
}

/**
 * Build the initial Conversation state, applying any provided overrides on top of the documented
 * defaults. Overrides come from the Conversation slice of AIChatProvider's own initialState prop
 * (which includes the composition-root-resolved currentConversationId + mode).
 */
export function getInitialConversationState(overrides: Partial<ConversationState> = {}): ConversationState {
  const state: ConversationState = {
    chatContext: { pageUrl: window.location.href },
    currentConversationId: null,
    conversations: [],
    streamStatus: STREAM_STATUS.IDLE,
    mode: detectConversationMode(window.location.pathname),
    messageQueue: [],
    canvasContent: '',
    conversationData: null,
    isCanvasToolExecuting: false,
    isLoadingConversation: false,
    lastFailedMessage: null,
    pills: [],
    ...overrides,
  };
  return state;
}

export function conversationReducer(state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case CONVERSATION_ACTIONS.SET_CHAT_CONTEXT:
      return { ...state, chatContext: action.payload };
    case CONVERSATION_ACTIONS.SET_CURRENT_CONVERSATION_ID:
      return { ...state, currentConversationId: action.payload };
    case CONVERSATION_ACTIONS.SET_CONVERSATIONS:
      return { ...state, conversations: action.payload };
    case CONVERSATION_ACTIONS.SET_STREAM_STATUS:
      return { ...state, streamStatus: action.payload };
    case CONVERSATION_ACTIONS.SET_CONVERSATION_MODE:
      // Only clear conversation if mode is actually changing
      if (state.mode !== action.payload) {
        return {
          ...state,
          mode: action.payload,
          // Clear current conversation when mode changes to prevent cross-mode usage
          currentConversationId: null,
          // Clear any pending messages
          messageQueue: [],
          // Reset conversation status
          streamStatus: STREAM_STATUS.IDLE,
          // Reset loading state to prevent stuck loading when switching modes
          isLoadingConversation: false,
        };
      }
      return state;
    case CONVERSATION_ACTIONS.QUEUE_MESSAGE:
      return { ...state, messageQueue: [...state.messageQueue, action.payload] };
    case CONVERSATION_ACTIONS.DEQUEUE_MESSAGE:
      return { ...state, messageQueue: state.messageQueue.slice(1) };
    case CONVERSATION_ACTIONS.CLEAR_MESSAGE_QUEUE:
      return { ...state, messageQueue: [] };
    case CONVERSATION_ACTIONS.SET_CANVAS_CONTENT:
      return { ...state, canvasContent: action.payload || '' };
    case CONVERSATION_ACTIONS.SET_CONVERSATION_DATA:
      return { ...state, conversationData: action.payload };
    case CONVERSATION_ACTIONS.CLEAR_CONVERSATION_DATA:
      return { ...state, conversationData: null };
    case CONVERSATION_ACTIONS.SET_CANVAS_TOOL_EXECUTING:
      return { ...state, isCanvasToolExecuting: action.payload };
    case CONVERSATION_ACTIONS.SET_IS_LOADING_CONVERSATION:
      return { ...state, isLoadingConversation: action.payload };
    case CONVERSATION_ACTIONS.SET_LAST_FAILED_MESSAGE:
      return { ...state, lastFailedMessage: action.payload };
    case CONVERSATION_ACTIONS.CLEAR_LAST_FAILED_MESSAGE:
      return { ...state, lastFailedMessage: null };
    case CONVERSATION_ACTIONS.ADD_PILL: {
      const newPill = action.payload;
      // Remove existing pill with same ID to prevent duplicates
      const filtered = state.pills.filter((p) => p.id !== newPill.id);
      // Add new pill and sort by priority (higher priority first)
      const updated = [...filtered, newPill].sort((a, b) => (b.priority || 0) - (a.priority || 0));
      return { ...state, pills: updated };
    }
    case CONVERSATION_ACTIONS.REMOVE_PILL:
      return { ...state, pills: state.pills.filter((p) => p.id !== action.payload.id) };
    case CONVERSATION_ACTIONS.REMOVE_PILLS_BY_TYPE:
      return { ...state, pills: state.pills.filter((p) => p.type !== action.payload.type) };
    case CONVERSATION_ACTIONS.CLEAR_PILLS:
      return { ...state, pills: [] };
    default:
      return state;
  }
}
